import { ReloadOutlined } from '@ant-design/icons';
import type { HqApi, ScreenDevicePendingRow } from '@hq/api-sdk';
import { formatDisplayTime } from '@hq/utils';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';

export interface ScreenDevicePendingPanelProps {
  hq: HqApi;
}

/**
 * 待确认的大屏申请（只读监控）：
 * 大屏首次打开会生成一条 PENDING 申请（库里只存编码的 sha256），
 * 运营/系统端在这里只能看到「谁、从哪、什么时候」申请，看不到编码——
 * 因此本面板不提供任何确认按钮，确认必须由人在屏前读到 12 位申请码，再到「申请码确认」页填入。
 * 这样即使账号有 sys:config:manage，也无法远程把陌生大屏绑定到自己机构。
 */
const REFETCH_MS = 20_000;

/** 剩余有效期按「服务端给出的 remainSeconds − 本地已流逝秒数」递减，避免每秒打接口。 */
function formatRemain(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const mm = String(Math.floor(total / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

export function ScreenDevicePendingPanel({ hq }: ScreenDevicePendingPanelProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const list = useQuery({
    queryKey: ['screen-device', 'pending'],
    queryFn: () => hq.tenant.screenDevicePending(),
    refetchInterval: REFETCH_MS
  });

  const remainOf = (row: ScreenDevicePendingRow) =>
    row.remainSeconds - Math.floor((now - list.dataUpdatedAt) / 1000);

  const columns: ColumnsType<ScreenDevicePendingRow> = [
    { title: '业务', dataIndex: 'bizName', width: 150 },
    {
      title: '大屏名称',
      dataIndex: 'deviceName',
      width: 180,
      render: (value: string | null) => value ?? '—'
    },
    {
      title: '来源 IP',
      dataIndex: 'requestIp',
      width: 140,
      render: (value: string | null) => value ?? '—'
    },
    {
      title: '客户端',
      dataIndex: 'requestUa',
      ellipsis: true,
      render: (value: string | null) =>
        value ? (
          <Tooltip title={value}>
            <Typography.Text ellipsis style={{ maxWidth: 240 }}>
              {value}
            </Typography.Text>
          </Tooltip>
        ) : (
          '—'
        )
    },
    {
      title: '申请时间',
      dataIndex: 'createAt',
      width: 160,
      render: (value: string | null) => (value ? formatDisplayTime(value) : '—')
    },
    {
      title: '剩余有效期',
      width: 120,
      render: (_, row) => {
        const remain = remainOf(row);
        if (remain <= 0) return <Tag>已过期</Tag>;
        return <Tag color={remain <= 60 ? 'red' : 'blue'}>{formatRemain(remain)}</Tag>;
      }
    }
  ];

  const rows = list.data ?? [];

  return (
    <Card
      title={
        <Space size={8}>
          <span>待确认大屏申请</span>
          {rows.length > 0 ? <Tag color="gold">{rows.length}</Tag> : null}
        </Space>
      }
      extra={
        <Button
          size="small"
          icon={<ReloadOutlined />}
          loading={list.isFetching}
          onClick={() => void list.refetch()}
        >
          刷新
        </Button>
      }
      style={{ marginBottom: 16 }}
    >
      {list.error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="待确认申请读取失败"
          description={list.error instanceof Error ? list.error.message : String(list.error)}
        />
      ) : (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="这里只做监控：不显示确认编码，因此无法在此远程确认。请到屏前读取大屏上的 12 位申请码，再到「申请码确认」页按 4-4-4 三段填入。"
        />
      )}
      <Table<ScreenDevicePendingRow>
        size="small"
        rowKey="id"
        loading={list.isLoading}
        dataSource={rows}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        scroll={{ x: 990 }}
        locale={{ emptyText: '暂无待确认的大屏申请' }}
        columns={columns}
      />
    </Card>
  );
}
